import { Link, Spinner, Text } from "@chakra-ui/react";
import { ProblemCode } from "@syla/shared/errors/ProblemCode";
import { isSylaApiError } from "@syla/shared/errors/SylaApiError";
import { throwError } from "@syla/shared/types/helpers/errors";
import React, { useContext, useEffect } from "react";
import { useMutation } from "react-query";
import { useHistory, useLocation } from "react-router";

import { UserContext } from "../../../contexts/UserContext";
import { Route, useNavigator } from "../../../routers/navigator";
import { FormContainer } from "../../atoms/Containers";
import { UnexpectedError } from "../../atoms/UnexpectedError";
import { SylaFormHeader } from "../forms/SylaFormHeader";

export const CompleteVerificationComponent = (): JSX.Element => {
  const { verify } = useContext(UserContext);
  const history = useHistory();
  const location = useLocation();
  const navigate = useNavigator();

  const searchParams = new URLSearchParams(location.search);
  const token = searchParams.get("token");
  const skipOnboarding = searchParams.get("skip-onboarding");

  const verification = useMutation(
    (token: string | null) =>
      verify(token ?? throwError(new Error("Token not present"))),
    {
      onSuccess: ({ defaultAccountId }) => {
        navigate({
          route: skipOnboarding ? Route.Home : Route.FirstLogin,
          accountId: defaultAccountId,
          replace: true,
        });
      },
    },
  );

  useEffect(() => {
    // only run once
    if (verification.isIdle) {
      verification.mutate(token);
    }
  }, [verification, token]);

  function renderError(error: unknown): JSX.Element {
    const errorColor = "red.500";

    if (isSylaApiError(error)) {
      switch (error.code) {
        case ProblemCode.EmailRegistrationExpired:
          return (
            <Text color={errorColor}>
              This verification link has expired.
              <Text>
                <Link
                  fontWeight="bold"
                  href="/sign-up"
                  onClick={() => history.replace("/sign-up")}
                >
                  Please sign up again.
                </Link>
              </Text>
            </Text>
          );

        case ProblemCode.RefreshedExpiredOtp:
          return (
            <Text color={errorColor}>
              This link has expired. We’ve sent another link to the email
              address you signed up with.
            </Text>
          );

        case ProblemCode.VerifiedUserAlreadyExists:
          return (
            <Text color={errorColor}>
              A verified user already exists with this email address.
            </Text>
          );
      }
    }

    return <UnexpectedError />;
  }

  return (
    <FormContainer>
      <SylaFormHeader
        heading={"Completing Verification"}
        subText={"of your new Syla Account"}
      />

      {verification.isLoading && (
        <Spinner
          color="primary.500"
          emptyColor="primary.200"
          my="10px"
          thickness="5px"
          size="lg"
        />
      )}

      {verification.isError && renderError(verification.error)}
    </FormContainer>
  );
};
